import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { ManualActivateSubscription } from "../../services/paymentTransaction.services";
import { SubscriptionPlanList } from "../../services/subscriptionPlan.services";
import { userList as UserList } from "../../services/user.service";
import Loader from "../../common/loader";

const ManualRecovery = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [loader, setLoader] = useState(false);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);

  const queryParams = new URLSearchParams(location.search);
  const initialUser = queryParams.get("user_id") || "";
  const initialProductId = queryParams.get("product_id") || "";
  const initialOriginalTxn = queryParams.get("original_transaction_id") || "";
  const initialTxnId = queryParams.get("transaction_id") || "";
  const initialPurchaseToken = queryParams.get("purchase_token") || "";
  const initialOrderId = queryParams.get("order_id") || "";
  const initialPlatform = queryParams.get("platform") || "apple";

  const [selectedUser, setSelectedUser] = useState(initialUser);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [platform, setPlatform] = useState(initialPlatform);
  const [transactionId, setTransactionId] = useState(initialTxnId);
  const [originalTransactionId, setOriginalTransactionId] = useState(initialOriginalTxn);
  const [purchaseToken, setPurchaseToken] = useState(initialPurchaseToken);
  const [orderId, setOrderId] = useState(initialOrderId);
  const [productId, setProductId] = useState(initialProductId);
  const [notes, setNotes] = useState("");

  const breadcrumbs = [
    { title: t("contact-us.link_dashboard"), url: "/admin/dashboard" },
    { title: "Manual Recovery", url: "" },
  ];

  useEffect(() => {
    const loadData = async () => {
      setLoader(true);
      try {
        const userFormData = new FormData();
        userFormData.append("page", 1);
        userFormData.append("per_page", 100);
        const usersData = await UserList(userFormData);
        setUsers(usersData?.data?.list || []);

        const planFormData = new FormData();
        planFormData.append("page", 1);
        planFormData.append("per_page", 100);
        const plansData = await SubscriptionPlanList(planFormData);
        setPlans(plansData?.data?.list || []);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoader(false);
      }
    };
    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser || !selectedPlan) {
      Swal.fire({
        ...SWAL_SETTINGS,
        icon: "warning",
        title: "Warning",
        text: "Please select user and plan",
      });
      return;
    }

    setLoader(true);
    try {
      const formData = new FormData();
      formData.append("user_id", selectedUser);
      formData.append("plan_id", selectedPlan);
      formData.append("platform", platform);

      if (transactionId) formData.append("transaction_id", transactionId);
      if (originalTransactionId) formData.append("original_transaction_id", originalTransactionId);
      if (purchaseToken) formData.append("purchase_token", purchaseToken);
      if (orderId) formData.append("order_id", orderId);
      if (productId) formData.append("product_id", productId);
      if (notes) formData.append("notes", notes);

      await ManualActivateSubscription(formData);

      Swal.fire({
        ...SWAL_SETTINGS,
        icon: "success",
        title: "Success",
        text: "Subscription activated successfully",
      });

      setSelectedUser("");
      setSelectedPlan("");
      setPlatform("apple");
      setTransactionId("");
      setOriginalTransactionId("");
      setPurchaseToken("");
      setOrderId("");
      setProductId("");
      setNotes("");
    } catch (error) {
      console.error("Error activating subscription:", error);
      Swal.fire({
        ...SWAL_SETTINGS,
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Failed to activate subscription",
      });
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <h6 className="main-content-label mb-4">Manual Subscription Recovery</h6>
            {loader ? (
              <Loader />
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">User *</label>
                    <select
                      className="form-control"
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      required
                    >
                      <option value="">Select User</option>
                      {users.map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.name} ({user.email || user.phone || "N/A"})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Subscription Plan *</label>
                    <select
                      className="form-control"
                      value={selectedPlan}
                      onChange={(e) => setSelectedPlan(e.target.value)}
                      required
                    >
                      <option value="">Select Plan</option>
                      {plans.map((plan) => (
                        <option key={plan._id} value={plan._id}>
                          {plan.plan_name} - ${plan.price}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Platform</label>
                    <select
                      className="form-control"
                      value={platform}
                      onChange={(e) => setPlatform(e.target.value)}
                    >
                      <option value="apple">Apple</option>
                      <option value="android">Android</option>
                      <option value="manual">Manual</option>
                    </select>
                  </div>

                  {platform !== "manual" && (
                    <>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Product ID</label>

                        <select
                          className="form-control"
                          value={productId}
                          onChange={(e) => setProductId(e.target.value)}
                        >
                          <option value="">Select Product ID</option>
                          {plans.map((plan) => (
                            <option key={plan._id} value={plan.product_id}>
                              {plan.plan_name} - {plan.product_id}
                            </option>
                          ))}
                        </select>
                        {/* <input
                          type="text"
                          className="form-control"
                          value={productId}
                          onChange={(e) => setProductId(e.target.value)}
                          placeholder="e.g. com.dinoland.app.subscription.monthly"
                        /> */}
                      </div>

                      {platform === "apple" && (
                        <>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Transaction ID</label>
                            <input
                              type="text"
                              className="form-control"
                              value={transactionId}
                              onChange={(e) => setTransactionId(e.target.value)}
                              placeholder="Apple transaction ID"
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Original Transaction ID</label>
                            <input
                              type="text"
                              className="form-control"
                              value={originalTransactionId}
                              onChange={(e) => setOriginalTransactionId(e.target.value)}
                              placeholder="Apple original transaction ID"
                            />
                          </div>
                        </>
                      )}

                      {platform === "android" && (
                        <>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Purchase Token</label>
                            <input
                              type="text"
                              className="form-control"
                              value={purchaseToken}
                              onChange={(e) => setPurchaseToken(e.target.value)}
                              placeholder="Google Play purchase token"
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Order ID</label>
                            <input
                              type="text"
                              className="form-control"
                              value={orderId}
                              onChange={(e) => setOrderId(e.target.value)}
                              placeholder="Google Play order ID"
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add notes about this manual activation"
                      rows={3}
                    />
                  </div>

                  <div className="col-md-12">
                    <button
                      type="submit"
                      className="btn btn-main-primary signbtn"
                      disabled={loader}
                    >
                      {loader ? "Activating..." : "Activate Subscription"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ManualRecovery;
